import Phaser from 'phaser';
import { PALETTE } from '../config';
import { createPortrait, type AthleteView } from '../game/AthleteView';
import { AudioEngine } from '../game/AudioEngine';
import { countryFlag } from '../game/data/flags';
import { SaveManager } from '../game/SaveManager';
import { SLOTS, cosmeticsBySlot, getCosmetic, variantKey, variantUrl, type Cosmetic, type CosmeticSlot } from '../game/data/cosmetics';
import { t } from '../game/i18n';
import type { Athlete } from '../game/types';
import { addBackButton } from '../ui/BackButton';
import { drawGradientBackground } from '../ui/Background';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

const SLOT_EMOJI: Record<CosmeticSlot, string> = { head: '🎩', aura: '✨', trail: '💫' };

/** Cosmetic shop: pick an athlete, buy vanity items with coins and equip them per slot (live preview). */
export class Cosmetics extends Phaser.Scene {
  private index = 0;
  private slot: CosmeticSlot = 'head';
  private rig?: AthleteView;
  private panelObjects: Phaser.GameObjects.GameObject[] = [];
  private actionObjects: Phaser.GameObjects.GameObject[] = [];
  /** The item currently TRIED ON (preview only) for the active slot; null = "none". Spending
   * happens only when the player presses Buy/Apply, never on selection. */
  private pending: string | null = null;

  constructor() {
    super('Cosmetics');
  }

  init(data: { index?: number; slot?: CosmeticSlot }): void {
    if (typeof data?.index === 'number') this.index = data.index;
    if (data?.slot && SLOTS.includes(data.slot)) this.slot = data.slot;
  }

  private roster(): readonly Athlete[] {
    return SaveManager.instance.ownedRoster();
  }

  create(): void {
    this.panelObjects = [];
    this.actionObjects = [];
    const n = this.roster().length;
    this.index = ((this.index % n) + n) % n;
    // Start the try-on at whatever is currently equipped for this slot.
    this.pending = SaveManager.instance.equippedFor(this.roster()[this.index]!.id)[this.slot] ?? null;
    this.build();
    const w0 = this.scale.width, h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart({ index: this.index, slot: this.slot });
    });
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);

    const groundH = Math.max(72, h * 0.12);
    const groundY = h - groundH;
    this.add.graphics()
      .fillStyle(PALETTE.track, 1).fillRect(0, groundY, w, groundH)
      .fillStyle(PALETTE.trackLine, 1).fillRect(0, groundY, w, 4)
      .fillStyle(PALETTE.laneAccent, 0.16).fillEllipse(w * 0.26, groundY + 6, w * 0.32, 36);

    this.add.text(cx, h * 0.06, `🛍 ${t('shop.title')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(30 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);

    this.coinChip(w - 16 * s, 12 * s, s);
    addBackButton(this, s, () => this.scene.start('MainMenu'));

    // Live athlete preview — shows the TRIED-ON selection (pending), not the saved state.
    const athlete = this.roster()[this.index]!;
    this.renderPreview(w * 0.24, groundY + 2);

    if (this.roster().length > 1) {
      this.arrow(Math.max(38, w * 0.05), h * 0.46, -1, s);
      this.arrow(w * 0.43, h * 0.46, 1, s);
    }
    this.dots(w * 0.24, groundY - 10 * s, s);
    // Athlete name.
    this.add.text(w * 0.24, h * 0.12, `${countryFlag(athlete.nationality)} ${athlete.name}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(16, Math.round(19 * s))}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 2, '#000000aa', 4);

    // Try-on hint above the slot tabs (centred over the items panel).
    this.add.text(w * 0.72, h * 0.135, t('shop.tryHint'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12.5 * s))}px`, color: '#9fb0e0',
      align: 'center', wordWrap: { width: w * 0.46 },
    }).setOrigin(0.5);

    this.slotTabs(s);
    this.renderItems();
    this.renderAction();
  }

  /** Equipped map for the PREVIEW: saved state with the active slot overridden by the try-on. */
  private previewEquipped(): Record<string, string> {
    const eq = { ...SaveManager.instance.equippedFor(this.roster()[this.index]!.id) };
    if (this.pending) eq[this.slot] = this.pending; else delete eq[this.slot];
    return eq;
  }

  /** (Re)build the athlete preview from the current try-on selection. */
  private renderPreview(x: number, y: number): void {
    const portraitH = Phaser.Math.Clamp(this.scale.height * 0.5, 220, 420);
    this.rig?.destroy();
    this.rig = createPortrait(this, x, y, this.roster()[this.index]!, portraitH, this.previewEquipped());
    this.rig.setMotion(0);
    this.rig.container.setDepth(5);
  }

  /** Slot tabs (Head / Aura / Trail) above the items panel. */
  private slotTabs(s: number): void {
    const { w, h } = viewport(this);
    const panelX = w * 0.48, panelW = w * 0.48;
    const y = h * 0.18;
    const fs = Math.max(13, Math.round(15 * s));
    const gap = 8 * s;
    const probe = SLOTS.map((sl) => {
      const label = `${SLOT_EMOJI[sl]} ${t(`slot.${sl}`)}`;
      const tx = this.add.text(-999, -999, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, fontStyle: 'bold' });
      const wd = tx.width + 22 * s; tx.destroy();
      return { sl, label, wd };
    });
    const total = probe.reduce((a, b) => a + b.wd, 0) + gap * (probe.length - 1);
    let x = panelX + panelW / 2 - total / 2;
    const hh = Math.max(32, 36 * s);
    for (const p of probe) {
      const active = this.slot === p.sl;
      const g = this.add.graphics();
      g.fillStyle(active ? PALETTE.laneAccent : PALETTE.ink, active ? 0.95 : 0.5).fillRoundedRect(x, y - hh / 2, p.wd, hh, hh / 2);
      g.lineStyle(1.5, active ? PALETTE.white : PALETTE.cadence, active ? 0.6 : 0.3).strokeRoundedRect(x, y - hh / 2, p.wd, hh, hh / 2);
      this.add.text(x + p.wd / 2, y, p.label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: active ? '#0b1020' : '#cfd8f5', fontStyle: 'bold' }).setOrigin(0.5);
      if (!active) {
        g.setInteractive(new Phaser.Geom.Rectangle(x, y - hh / 2, p.wd, hh), Phaser.Geom.Rectangle.Contains)
          .on(Phaser.Input.Events.POINTER_DOWN, () => { AudioEngine.tap(0.3); this.scene.restart({ index: this.index, slot: p.sl }); });
      }
      x += p.wd + gap;
    }
  }

  /** The buy/equip list for the active slot (rebuildable). */
  private renderItems(): void {
    const { w, h, s } = viewport(this);
    this.panelObjects.forEach((o) => o.destroy());
    this.panelObjects = [];
    const save = SaveManager.instance;
    const athlete = this.roster()[this.index]!;
    const equippedId = save.equippedFor(athlete.id)[this.slot] ?? null;

    const panelX = w * 0.48, panelW = w * 0.48, panelCx = panelX + panelW / 2;
    const top = h * 0.23;
    const bottom = h - Math.max(72, h * 0.12) - 6 * s;
    this.panelObjects.push(drawPanel(this, panelCx, (top + bottom) / 2, panelW, bottom - top, { radius: 16, fillAlpha: 0.66 }));

    const items: (Cosmetic | null)[] = [null, ...cosmeticsBySlot(this.slot)];
    const cols = 2;
    const rows = Math.ceil(items.length / cols);
    const gx = 14 * s, gy = 10 * s;
    const cellW = (panelW - 28 * s - gx) / cols;
    const cellH = Math.min(64 * s, (bottom - top - 24 * s - gy * (rows - 1)) / rows);
    const x0 = panelX + 14 * s, y0 = top + 14 * s;

    items.forEach((item, i) => {
      const col = i % cols, row = Math.floor(i / cols);
      const x = x0 + col * (cellW + gx), y = y0 + row * (cellH + gy);
      const id = item ? item.id : null;
      this.itemCard(item, x, y, cellW, cellH, s, id === this.pending, id === equippedId, save.coins);
    });
  }

  private itemCard(item: Cosmetic | null, x: number, y: number, cw: number, ch: number, s: number, selected: boolean, equipped: boolean, coins: number): void {
    const save = SaveManager.instance;
    const owned = item ? save.ownsCosmetic(item.id) : true;
    const afford = item ? coins >= item.price : true;
    const g = this.add.graphics();
    // Border highlights the TRIED-ON selection (cadence); a green tint marks what's actually saved.
    const base = equipped ? PALETTE.good : PALETTE.bgTop;
    g.fillStyle(0x000000, 0.25).fillRoundedRect(x, y + 3, cw, ch, 12);
    g.fillStyle(base, equipped ? 0.3 : 0.5).fillRoundedRect(x, y, cw, ch, 12);
    g.lineStyle(selected ? 3 : 2, selected ? PALETTE.laneAccent : (equipped ? PALETTE.good : PALETTE.cadence), selected ? 1 : (equipped ? 0.7 : 0.25)).strokeRoundedRect(x, y, cw, ch, 12);
    this.panelObjects.push(g);

    // Colour swatch.
    const sw = ch * 0.5;
    if (item) {
      this.panelObjects.push(this.add.graphics()
        .fillStyle(item.color, 1).fillCircle(x + 14 * s + sw / 2, y + ch / 2, sw / 2)
        .lineStyle(2, 0xffffff, 0.3).strokeCircle(x + 14 * s + sw / 2, y + ch / 2, sw / 2));
    } else {
      this.panelObjects.push(this.add.text(x + 14 * s + sw / 2, y + ch / 2, '∅', { fontFamily: 'system-ui', fontSize: `${Math.round(sw)}px`, color: '#8893b8' }).setOrigin(0.5));
    }

    const nameX = x + 22 * s + sw;
    const name = item ? t(item.nameKey) : t('shop.none');
    const nameFs = Math.max(13, Math.round(15 * s));
    const nm = this.add.text(nameX, y + ch * 0.32, name, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${nameFs}px`, color: '#f4f7ff', fontStyle: 'bold' }).setOrigin(0, 0.5);
    if (nm.width > cw - (nameX - x) - 8 * s) nm.setScale((cw - (nameX - x) - 8 * s) / nm.width);
    this.panelObjects.push(nm);

    // Status line: what's saved/owned/priced (NOT an action — the action is the bottom button).
    let sub = '', col = '#9fb0e0';
    if (equipped) { sub = `✓ ${t('shop.equipped')}`; col = '#6ef0a0'; }
    else if (!item) { sub = ''; }
    else if (owned) { sub = t('shop.ownedTag'); col = '#cfd8f5'; }
    else { sub = `${item.price} 🪙`; col = afford ? '#ffd24a' : '#8893b8'; }
    if (sub) this.panelObjects.push(this.add.text(nameX, y + ch * 0.7, sub, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round(13 * s))}px`, color: col, fontStyle: 'bold' }).setOrigin(0, 0.5));

    g.setInteractive(new Phaser.Geom.Rectangle(x, y, cw, ch), Phaser.Geom.Rectangle.Contains)
      .on(Phaser.Input.Events.POINTER_DOWN, () => this.select(item));
  }

  /** Try on an item (preview only — never spends). Loads a head variant sprite on demand. */
  private select(item: Cosmetic | null): void {
    const next = item ? item.id : null;
    if (next === this.pending) return;
    this.pending = next;
    AudioEngine.tap(0.35);
    const rebuild = (): void => { this.renderPreview(this.scale.width * 0.24, this.scale.height - Math.max(72, this.scale.height * 0.12) + 2); this.renderItems(); this.renderAction(); };
    if (this.slot === 'head' && item?.accessory) {
      const key = variantKey(this.roster()[this.index]!.skin, item.accessory);
      if (!this.textures.exists(key)) {
        this.load.image(key, variantUrl(this.roster()[this.index]!.skin, item.accessory));
        this.load.once(Phaser.Loader.Events.COMPLETE, rebuild);
        this.load.start();
        return;
      }
    }
    rebuild();
  }

  /** Bottom action button: Buy (if the tried-on item isn't owned) / Apply (equip) / disabled when
   * the selection already matches what's saved. This is the ONLY place coins are spent. */
  private renderAction(): void {
    const { w, h, s } = viewport(this);
    this.actionObjects.forEach((o) => o.destroy());
    this.actionObjects = [];
    const save = SaveManager.instance;
    const athlete = this.roster()[this.index]!;
    const savedId = save.equippedFor(athlete.id)[this.slot] ?? null;
    const item = this.pending ? getCosmetic(this.pending) : null;
    const owned = item ? save.ownsCosmetic(item.id) : true;

    let label: string, color: number, enabled = true;
    if (this.pending === savedId) { label = `✓ ${t('shop.equipped')}`; color = PALETTE.trackLine; enabled = false; }
    else if (item && !owned) {
      const afford = save.coins >= item.price;
      label = `${t('shop.buy')} · ${item.price} 🪙`; color = afford ? PALETTE.good : PALETTE.trackLine; enabled = afford;
    } else { label = t('shop.apply'); color = PALETTE.laneAccent; }

    const bx = w * 0.24, by = h - Math.max(72, h * 0.12) * 0.5;
    const bw = Math.min(w * 0.4, 320), bh = Math.max(46, Math.min(54 * s, Math.max(72, h * 0.12) * 0.8));
    const g = this.add.graphics().setDepth(20);
    g.fillStyle(0x000000, 0.3).fillRoundedRect(bx - bw / 2, by - bh / 2 + 3, bw, bh, bh / 2);
    g.fillStyle(color, enabled ? 1 : 0.5).fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, bh / 2);
    this.actionObjects.push(g);
    const txt = this.add.text(bx, by, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(15, Math.round(19 * s))}px`, color: enabled ? '#0b1020' : '#9fb0e0', fontStyle: 'bold' }).setOrigin(0.5).setDepth(21);
    this.actionObjects.push(txt);
    if (enabled) {
      g.setInteractive(new Phaser.Geom.Rectangle(bx - bw / 2, by - bh / 2, bw, bh), Phaser.Geom.Rectangle.Contains)
        .on(Phaser.Input.Events.POINTER_DOWN, () => this.commit());
    }
  }

  /** Commit the tried-on selection: buy (if needed) then equip, spending coins now. */
  private commit(): void {
    const save = SaveManager.instance;
    const athlete = this.roster()[this.index]!;
    const item = this.pending ? getCosmetic(this.pending) : null;
    if (item && !save.ownsCosmetic(item.id)) {
      if (!save.buyCosmetic(item.id, item.price)) { AudioEngine.tap(0.2); this.cameras.main.shake(160, 0.005); return; }
      AudioEngine.chime(); this.cameras.main.flash(160, 110, 240, 160);
    } else {
      AudioEngine.tap(0.5);
    }
    save.equipCosmetic(athlete.id, this.slot, this.pending);
    // The preview already loaded any head variant, so a plain refresh renders correctly.
    this.scene.restart({ index: this.index, slot: this.slot });
  }

  private coinChip(rightX: number, y: number, s: number): void {
    const fs = Math.round(Math.max(17, 19 * s));
    const tx = this.add.text(0, 0, `🪙 ${SaveManager.instance.coins}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' }).setShadow(0, 2, '#000000cc', 3);
    const padX = 14 * s, ww = tx.width + padX * 2, hh = tx.height + 12 * s, bx = rightX - ww;
    this.add.graphics().setDepth(18)
      .fillStyle(PALETTE.ink, 0.78).fillRoundedRect(bx, y, ww, hh, hh / 2)
      .lineStyle(2, PALETTE.cadence, 0.6).strokeRoundedRect(bx, y, ww, hh, hh / 2);
    tx.setPosition(bx + padX, y + hh / 2).setOrigin(0, 0.5).setDepth(19);
  }

  private arrow(x: number, y: number, dir: number, s: number): void {
    const r = Math.max(28, 32 * s);
    const c = this.add.circle(x, y, r, PALETTE.ink, 0.45).setStrokeStyle(2, PALETTE.cadence, 0.7).setDepth(9);
    const tx = this.add.text(x, y, dir < 0 ? '‹' : '›', { fontFamily: 'system-ui', fontSize: `${Math.round(40 * s)}px`, color: '#59d2ff', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9);
    [c, tx].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.cycle(dir)));
  }

  private dots(cx: number, y: number, s: number): void {
    const n = this.roster().length;
    const gap = Math.min(16 * s, (this.scale.width * 0.4) / Math.max(n, 1));
    for (let i = 0; i < n; i++) this.add.circle(cx + (i - (n - 1) / 2) * gap, y, i === this.index ? 5 * s : 3.5 * s, i === this.index ? PALETTE.laneAccent : PALETTE.trackLine, 1).setDepth(9);
  }

  private cycle(dir: number): void {
    const n = this.roster().length;
    this.index = (this.index + dir + n) % n;
    AudioEngine.tap(0.3);
    this.scene.restart({ index: this.index, slot: this.slot });
  }

  update(_t: number, delta: number): void {
    this.rig?.update(delta / 1000);
  }
}
